import { useState, useEffect } from 'react';
import { Modal, Toggle } from '@carbon/react';
import { useStore, useAtomValue } from 'jotai';
import { format0 } from 'utils/number';
import { allDatasetAtoms, setDatasetVisibleAtom } from './atoms';
import { datasetRegistry } from './datasets';
import type { DatasetConfig } from './types';
import styles from './datasets-modal.module.scss';

const entries = Object.entries(datasetRegistry);

interface Props {
  open: boolean;
  onClose: () => void;
}

interface RowProps {
  id: string;
  config: DatasetConfig;
  checked: boolean;
  isLastActive: boolean;
  onToggle: (checked: boolean) => void;
}

// A row subscribes to its dataset's record count and layer color so the modal
// re-renders live as a search resolves or a layer is recoloured. Reading these
// via the store in the parent's render path would take a one-shot snapshot.
const DatasetRow = ({
  id,
  config,
  checked,
  isLastActive,
  onToggle,
}: RowProps) => {
  const atoms = allDatasetAtoms[id];
  const total = useAtomValue(atoms.searchAtom)?.data?.totalCount;
  const color = useAtomValue(atoms.layerColorAtom);

  return (
    <div className={styles.row}>
      <span className={styles.dot} style={{ backgroundColor: color }} />
      <div className={styles.info}>
        <span className={styles.name}>{config.label}</span>
        {total !== undefined && (
          <span className={styles.count}>{`${format0(total)} records`}</span>
        )}
      </div>
      <div className={styles.control}>
        <Toggle
          id={`dataset-toggle-${id}`}
          size="sm"
          hideLabel
          labelText={`Show ${config.label}`}
          toggled={checked}
          disabled={isLastActive}
          onToggle={onToggle}
        />
        {isLastActive && (
          <span className={styles.hint}>Cannot remove last active dataset</span>
        )}
      </div>
    </div>
  );
};

/**
 * Toggles which datasets render on the map and results tabs. Edits a local
 * draft seeded from each dataset's `scopedLayerVisibleAtom`; commits on Done.
 * @return {JSX.Element} The datasets visibility modal.
 */
export const DatasetsModal = ({ open, onClose }: Props) => {
  const store = useStore();
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    const seeded: Record<string, boolean> = {};
    for (const [id, atoms] of Object.entries(allDatasetAtoms)) {
      seeded[id] = store.get(atoms.scopedLayerVisibleAtom);
    }
    setDraft(seeded);
  }, [open, store]);

  const activeCount = Object.values(draft).filter(Boolean).length;

  const handleDone = () => {
    for (const id of Object.keys(allDatasetAtoms)) {
      if (draft[id] !== undefined) {
        store.set(setDatasetVisibleAtom, { datasetId: id, visible: draft[id] });
      }
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      size="sm"
      modalHeading="Datasets"
      primaryButtonText="Done"
      primaryButtonDisabled={activeCount === 0}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleDone}
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
    >
      <p className={styles.subtitle}>
        {activeCount === 0
          ? 'Select at least one dataset to display on the map and table.'
          : `Select the datasets to display on the map and table. (${activeCount} of ${entries.length} active)`}
      </p>
      <div className={styles.list}>
        {entries.map(([id, config]) => (
          <DatasetRow
            key={id}
            id={id}
            config={config}
            checked={draft[id] ?? true}
            isLastActive={(draft[id] ?? true) && activeCount === 1}
            onToggle={(checked) => setDraft((d) => ({ ...d, [id]: checked }))}
          />
        ))}
      </div>
    </Modal>
  );
};
